var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AskOkMessage, BroadcastToPeerMessage, convertToNumber3, convertToNumber4, DeleteModelMessage, FacetVisibility, PlaySoundMessage, RemoveFacetFromModelMessage, RenderColorMessage, RenderTransparencyMessage, UpdateFacetOnModelMessage, VisibilityFacet, ScriptComposeFacet, } from "flavr-common";
import { Quaternion, Vector3, Matrix4 } from "flavr-math";
import { DropModelMessage, PickupModelMessage, PickupModelResponseMessage, RequestCanPickupModelMessage } from "@messages/attachment/AttachmentMessage";
import { determineModelNameForPrinting, modelPool, useExternalMessages, computeLocalMatrixFromWorldTransformComponents } from "@modules/model";
import { networkEntitiesService } from "@modules/network/NetworkEntitiesService";
import { coreUserService } from "@modules/users";
import { busService } from "@modules/bus";
import { resolveUid } from "./Utils";
import { scriptService } from "../services/ScriptService";
const busConnection = busService.connection("ScriptModel");
export class ScriptModel {
    constructor(modelName, parent) {
        var _a;
        this.broadcastSound = (url, params) => {
            if ((scriptService.context === undefined) || (this.modelUID === undefined))
                return false;
            const msg = new PlaySoundMessage(this.modelUID, url, params);
            scriptService.context.pendingMessages.push(msg);
            scriptService.context.pendingMessages.push(new BroadcastToPeerMessage([msg]));
            return true;
        };
        if (modelName === undefined)
            return;
        let parentUID;
        if ((parent === null || parent === void 0 ? void 0 : parent.modelUID) !== undefined) {
            parentUID = parent.uid === undefined ? parent.modelUID : parent.uid;
        }
        else if (((_a = scriptService.context) === null || _a === void 0 ? void 0 : _a.currentRoom) !== undefined) {
            parentUID = scriptService.context.currentRoom.roomUID;
        }
        if (parentUID === undefined)
            return;
        const parentModel = modelPool.getModel(parentUID);
        if (parentModel === undefined)
            return;
        let model = parentModel.getChild(modelName);
        if (model === undefined) {
            const messages = [];
            const props = useExternalMessages(messages);
            model = modelPool.createModel(modelName, parentModel, props);
            busConnection.sendMultiple(messages);
        }
        this.modelUID = model.getUID();
        modelPool.setOnModelDestroyedHandler((_model, _destroyPermanently, _meta) => {
            this.destroy();
        });
    }
    /// Getters ///
    get name() {
        var _a;
        return (_a = this.getName()) !== null && _a !== void 0 ? _a : "UninitializedModel";
    }
    get uid() {
        return this.modelUID;
    }
    get parent() {
        if ((this.modelUID === undefined))
            return undefined;
        const model = modelPool.getModelOrThrow(this.modelUID);
        if (model.isRoom())
            return undefined;
        const modelParent = model.getParent();
        if (modelParent === undefined)
            return undefined;
        return this.createModelInstance(modelParent.getUID());
    }
    get children() {
        if ((this.modelUID === undefined))
            return [];
        const model = modelPool.getModelOrThrow(this.modelUID);
        if (model.isRoom())
            return [];
        const modelChildren = model.getChildren();
        return modelChildren
            .map(modelChild => this.createModelInstance(modelChild.getUID()))
            .filter(modelChild => modelChild !== undefined);
    }
    get isNull() {
        return (this.modelUID === undefined);
    }
    get isNotNull() {
        return !(this.modelUID === undefined);
    }
    /// General ///
    getRelativeId() {
        var _a;
        return (this.modelUID !== undefined)
            ? (_a = modelPool.getModel(this.modelUID)) === null || _a === void 0 ? void 0 : _a.getName()
            : undefined;
    }
    getName() {
        var _a;
        return (this.modelUID !== undefined)
            ? (_a = modelPool.getModel(this.modelUID)) === null || _a === void 0 ? void 0 : _a.getName()
            : undefined;
    }
    clone(relativeId, parentScriptModel) {
        return __awaiter(this, void 0, void 0, function* () {
            if ((scriptService.context === undefined) || (this.modelUID === undefined))
                return undefined;
            try {
                const parentUID = resolveUid(parentScriptModel);
                const model = modelPool.getModel(this.modelUID);
                if (model === undefined)
                    return undefined;
                const parent = modelPool.getModel(parentUID);
                const name = relativeId;
                const clonedModel = modelPool.cloneModel(model, Object.assign({ parent,
                    name }, this.getBroadcastProps()));
                if (clonedModel === undefined)
                    return undefined;
                // We add the instance to the context
                const instance = this.createModelInstance(clonedModel.getUID());
                const scriptFacet = clonedModel.getFacet(ScriptComposeFacet);
                if (scriptFacet !== undefined && (instance === null || instance === void 0 ? void 0 : instance.uid) !== undefined) {
                    yield scriptService.context.updateModelScripts(instance.uid, scriptFacet.scripts, new Date().getTime());
                }
                scriptService.context.flushMessages();
                return instance;
            }
            catch (error) {
                console.error(error);
                return undefined;
            }
        });
    }
    // ToDo: is this the same as destroyMe from ModelScriptFunctions?
    destroy(force = false) {
        if ((scriptService.context === undefined) || (this.modelUID === undefined))
            return;
        const model = scriptService.context.getModelInCurrentRoom(this.modelUID);
        if (model !== undefined) {
            for (const script of model.scripts.values()) {
                script.destroy();
            }
        }
        const msg = new DeleteModelMessage(this.modelUID);
        if (force) {
            scriptService.context.connection.send(msg);
        }
        else {
            scriptService.context.pendingMessages.push(msg);
        }
    }
    /// Find script ///
    getScriptName() {
        if ((scriptService.context === undefined) || (this.modelUID === undefined))
            return undefined;
        return scriptService.context.getScriptName(this.modelUID);
    }
    getScriptByClassName(className) {
        if ((scriptService.context === undefined) || (this.modelUID === undefined)) {
            return undefined;
        }
        const model = scriptService.context.getModelInCurrentRoom(this.modelUID);
        if (model === undefined) {
            return undefined;
        }
        for (const script of model.scripts.values()) {
            const instance = script.scriptInstance;
            if (instance === undefined)
                continue;
            if (script.scriptName === className) {
                return scriptService.context.wrapWeakScript(script);
            }
        }
        return undefined;
    }
    /// Emit function ///
    emit(funcName, ...params) {
        if ((scriptService.context === undefined) || (this.modelUID === undefined))
            return false;
        const model = scriptService.context.getModelInCurrentRoom(this.modelUID);
        if (!model)
            return false;
        let hasListeners = false;
        for (const script of model.scripts.values()) {
            const instance = script.scriptInstance;
            if (instance === undefined)
                continue;
            const func = instance[funcName];
            if (typeof func !== "function")
                continue;
            func.call(instance, ...params);
            hasListeners = true;
        }
        return hasListeners;
    }
    // ToDo: emitParent from ModelScriptFunctions is missing - is this intentional?
    // ToDo: emitAll from ModelScriptFunctions is missing - is this intentional?
    // ToDo: emitTo from ModelScriptFunctions is missing - is this intentional?
    // =========================================================================
    // ModelScriptFunctions: Common model methods
    // =========================================================================
    /// Find model ///
    getChildModelByRelativeId(relativeId) {
        if ((scriptService.context === undefined) || (this.modelUID === undefined))
            return undefined;
        // ToDo: validate relativeId
        const model = modelPool.getModel(this.modelUID);
        if (scriptService.context.getModelInCurrentRoom(this.modelUID) === undefined)
            return undefined;
        const child = model === null || model === void 0 ? void 0 : model.getChild(relativeId);
        if (child === undefined)
            return undefined;
        return this.createModelInstance(child.getUID());
    }
    findModelsByName(nameOrPart, options) {
        return modelPool.findModelsWithName(nameOrPart, options)
            .map(model => this.createModelInstance(model.getUID()))
            .filter(model => model !== undefined);
    }
    // ToDO: Remaining methods from ModelScriptFunctions missing - is this intentional?
    /// Facet ///
    updateFacet(facet, force = false) {
        if ((scriptService.context === undefined) || (this.modelUID === undefined))
            return;
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined) {
            console.error(`[scripting] Cannot update facet ${facet.type} because the model ${determineModelNameForPrinting(this.modelUID)} doesn't exist`);
            return;
        }
        if (!force) {
            const oldFacet = model.getFacet(facet);
            if (oldFacet !== undefined && oldFacet !== facet && oldFacet.equals(facet))
                return;
        }
        model.setFacet(facet, this.getBroadcastProps());
    }
    removeFacet(facetType) {
        if ((scriptService.context === undefined) || (this.modelUID === undefined))
            return;
        scriptService.context.pendingMessages.push(new RemoveFacetFromModelMessage(this.modelUID, facetType));
    }
    getFacet(facetType) {
        var _a;
        if ((this.modelUID === undefined))
            return undefined;
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined)
            return undefined;
        return (_a = model.getFacet(facetType)) === null || _a === void 0 ? void 0 : _a.clone();
    }
    // ToDO: hasFacet from ModelScriptFunctions missing - is this intentional?
    setFacetVisibility(type, visible) {
        if ((scriptService.context === undefined) || (this.modelUID === undefined))
            return;
        scriptService.context.pendingMessages.push(new UpdateFacetOnModelMessage(this.modelUID, new FacetVisibility(type, visible)));
    }
    /// Material ///
    getVisibility() {
        var _a;
        if ((scriptService.context === undefined) || (this.modelUID === undefined))
            return false;
        const model = modelPool.getModel(this.modelUID);
        const facet = model === null || model === void 0 ? void 0 : model.getFacet(VisibilityFacet);
        return (_a = facet === null || facet === void 0 ? void 0 : facet.visible) !== null && _a !== void 0 ? _a : true;
    }
    // ToDo: either rename this to setVisibility or rename getVisibility -> getVisible
    setVisible(visible) {
        if ((scriptService.context === undefined) || (this.modelUID === undefined))
            return;
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined) {
            console.warn(`[script] Couldn't change visibility of ${determineModelNameForPrinting(this.modelUID)} because it doesn't exist`);
            return;
        }
        // ToDo: not sure if updateFacet is necessary here. Test with FlaVR-Fortuna-LFS Dachstuhl scene
        model.setVisible(visible, Object.assign({ handleDescendants: true, updateFacet: true }, useExternalMessages(scriptService.context.pendingMessages)));
    }
    // Todo: Is this supposed to be the same method setMaterialColor in ModelScriptFunctions??
    setColor(color) {
        if ((scriptService.context === undefined) || (this.modelUID === undefined))
            return;
        scriptService.context.pendingMessages.push(new RenderColorMessage(this.modelUID, color));
    }
    setMaterialParameter(key, value, expectedType, materialIndex) {
        if (this.modelUID === undefined)
            return false;
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined)
            return false;
        return model.setMaterialParameter(key, value, expectedType, materialIndex);
    }
    getMaterialParameter(key, expectedType, materialIndex) {
        if (this.modelUID === undefined)
            return undefined;
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined)
            return undefined;
        return model.getMaterialParameter(key, expectedType, materialIndex);
    }
    setOpacity(opacity) {
        if ((scriptService.context === undefined) || (this.modelUID === undefined))
            return;
        scriptService.context.pendingMessages.push(new RenderTransparencyMessage(this.modelUID, 1 - opacity));
    }
    /// Transform model ///
    setPosition(pos) {
        if (this.modelUID === undefined)
            return;
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined) {
            console.error(`[scripting] Cannot set position because the model ${determineModelNameForPrinting(this.modelUID)} doesn't exist`);
            return;
        }
        model.setPosition(pos, this.getBroadcastProps());
    }
    setOrientation(ori) {
        if (this.modelUID === undefined)
            return;
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined) {
            console.error(`[scripting] Cannot set position because the model ${determineModelNameForPrinting(this.modelUID)} doesn't exist`);
            return;
        }
        model.setOrientation(ori, this.getBroadcastProps());
    }
    setScale(scale) {
        var _a;
        if (this.modelUID === undefined)
            return;
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined) {
            console.error(`[scripting] Cannot set position because the model ${determineModelNameForPrinting(this.modelUID)} doesn't exist`);
            return;
        }
        model.setScale(scale, this.getBroadcastProps());
        (_a = scriptService.context) === null || _a === void 0 ? void 0 : _a.flushMessages();
    }
    setTransform(pos, ori, scale) {
        if (this.modelUID === undefined)
            return;
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined) {
            console.error(`[scripting] Cannot set position because the model ${determineModelNameForPrinting(this.modelUID)} doesn't exist`);
            return;
        }
        model.setTransformComponents(pos, ori, scale, this.getBroadcastProps());
    }
    setWorldPosition(position) {
        if (this.modelUID === undefined)
            return;
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined) {
            console.error(`[scripting] Cannot set position because the model ${determineModelNameForPrinting(this.modelUID)} doesn't exist`);
            return;
        }
        const localMatrix = computeLocalMatrixFromWorldTransformComponents(model, position, undefined, undefined);
        model.setTransform(localMatrix, this.getBroadcastProps());
    }
    setWorldOrientation(orientation) {
        if (this.modelUID === undefined)
            return;
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined) {
            console.error(`[scripting] Cannot set position because the model ${determineModelNameForPrinting(this.modelUID)} doesn't exist`);
            return;
        }
        const localMatrix = computeLocalMatrixFromWorldTransformComponents(model, undefined, orientation, undefined);
        model.setTransform(localMatrix, this.getBroadcastProps());
    }
    setWorldScale(scale) {
        if (this.modelUID === undefined)
            return;
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined) {
            console.error(`[scripting] Cannot set position because the model ${determineModelNameForPrinting(this.modelUID)} doesn't exist`);
            return;
        }
        const localMatrix = computeLocalMatrixFromWorldTransformComponents(model, undefined, undefined, scale);
        model.setTransform(localMatrix, this.getBroadcastProps());
    }
    setWorldTransform(position, orientation, scale) {
        if (this.modelUID === undefined)
            return;
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined) {
            console.error(`[scripting] Cannot set position because the model ${determineModelNameForPrinting(this.modelUID)} doesn't exist`);
            return;
        }
        const localMatrix = computeLocalMatrixFromWorldTransformComponents(model, position, orientation, scale);
        model.setTransform(localMatrix, this.getBroadcastProps());
    }
    getPosition(result = new Vector3) {
        if (this.modelUID === undefined)
            return result.set(0, 0, 0);
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined)
            return result.set(0, 0, 0);
        return model.getPosition(result);
    }
    getOrientation(result = new Quaternion()) {
        if (this.modelUID === undefined)
            return result.identity();
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined)
            return result.identity();
        return model.getOrientation(result);
    }
    getScale(result = new Vector3) {
        if (this.modelUID === undefined)
            return result.set(1, 1, 1);
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined)
            return result.set(1, 1, 1);
        return model.getScale(result);
    }
    // ToDo: getMatrix is missing
    getWorldPosition(result = new Vector3()) {
        if (this.modelUID === undefined)
            return result.set(0, 0, 0);
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined)
            return result.set(0, 0, 0);
        return model.getWorldPosition(result);
    }
    getWorldOrientation(result = new Quaternion()) {
        if (this.modelUID === undefined) {
            console.warn('[script] ScriptModel.getWorldOrientation has no modelUID');
            return result.identity();
        }
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined) {
            console.warn(`[script] ScriptModel.getWorldOrientation has no model for modelUID ${this.modelUID}`);
            return result.identity();
        }
        return model.getWorldOrientation(result);
    }
    getWorldScale(result = new Vector3()) {
        if (this.modelUID === undefined)
            return result.set(1, 1, 1);
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined)
            return result.set(1, 1, 1);
        return model.getWorldScale(result);
    }
    getWorldMatrix(result = new Matrix4()) {
        if (this.modelUID === undefined)
            return result.identity();
        const model = modelPool.getModel(this.modelUID);
        if (model === undefined)
            return result.identity();
        return model.getWorldTransform(result);
    }
    // =========================================================================
    // ModelScriptFunctions: Additional methods
    // =========================================================================
    /// Audio ///
    playSound(url, params) {
        if ((scriptService.context === undefined) || (this.modelUID === undefined))
            return false;
        scriptService.context.pendingMessages.push(new PlaySoundMessage(this.modelUID, url, params));
        return true;
    }
    /// Network ///
    // ToDo: localOnly missing - is this intentional?
    setNetworked() {
        if ((this.modelUID === undefined))
            return false;
        return networkEntitiesService.createNetworkEntity(this.modelUID);
    }
    takeOwnership(destroyWhenOwnerDisconnect) {
        return __awaiter(this, void 0, void 0, function* () {
            if ((this.modelUID === undefined))
                return false;
            return yield networkEntitiesService.takeOwnership(this.modelUID, destroyWhenOwnerDisconnect);
        });
    }
    surrenderOwnership() {
        if ((this.modelUID === undefined))
            return false;
        return networkEntitiesService.surrenderOwnership(this.modelUID);
    }
    // ToDo: isHost missing - is this intentional?
    isMine() {
        if ((this.modelUID === undefined))
            return false;
        return networkEntitiesService.isMine(this.modelUID);
    }
    hasNetworkOwner() {
        if ((this.modelUID === undefined))
            return false;
        const entity = networkEntitiesService.getNetworkEntity(this.modelUID);
        if (entity === undefined)
            return false;
        if (entity.facet.ownerId === undefined)
            return false;
        return coreUserService.getUserByClientIdx(entity.facet.ownerId) !== undefined;
    }
    controlledByOthers() {
        if ((this.modelUID === undefined))
            return false;
        return networkEntitiesService.controlledByOthers(this.modelUID);
    }
    getNetworkOwner() {
        if ((this.modelUID === undefined))
            return false;
        const entity = networkEntitiesService.getNetworkEntity(this.modelUID);
        if (entity === undefined)
            return undefined;
        if (entity.facet.ownerId === undefined)
            return undefined;
        const user = coreUserService.getUserByClientIdx(entity.facet.ownerId);
        if (user === undefined)
            return undefined;
        return user.clientId;
    }
    getNetworkCreator() {
        if ((this.modelUID === undefined))
            return false;
        const entity = networkEntitiesService.getNetworkEntity(this.modelUID);
        if (!entity)
            return undefined;
        if (entity.facet.creatorId === undefined)
            return undefined;
        const user = coreUserService.getUserByClientIdx(entity.facet.creatorId);
        if (!user)
            return undefined;
        return user.clientId;
    }
    /// Pickup model ///
    canPickup(controllerUid) {
        return __awaiter(this, void 0, void 0, function* () {
            if ((scriptService.context === undefined) || (this.modelUID === undefined))
                return false;
            try {
                yield scriptService.context.connection.askByMessageType(new RequestCanPickupModelMessage(this.modelUID, controllerUid), AskOkMessage);
            }
            catch (_a) {
                return false;
            }
            return true;
        });
    }
    pickup() {
        return __awaiter(this, arguments, void 0, function* (params = {}) {
            if ((scriptService.context === undefined) || (this.modelUID === undefined))
                return undefined;
            let pickupOffset;
            if (params.offset !== undefined) {
                pickupOffset = {
                    position: convertToNumber3(params.offset.position),
                    orientation: convertToNumber4(params.offset.orientation),
                    scale: convertToNumber3(params.offset.scale)
                };
            }
            let modelUID = this.modelUID;
            try {
                const response = yield scriptService.context.connection.askByMessageType(new PickupModelMessage(this.modelUID, {
                    controllerUid: params.controllerUid,
                    offset: pickupOffset,
                    allowQuickReplacement: params.allowQuickReplacement,
                    dropOffMode: params.dropOffMode,
                    dropOffLayers: params.dropOffLayers,
                    dropOffButton: params.dropOffButton,
                    createTemporaryModel: params.createTemporaryModel,
                    deleteOnDropOff: params.deleteOnDropOff,
                }), PickupModelResponseMessage);
                modelUID = response.modelUID;
            }
            catch (error) {
                console.warn(error);
                return undefined;
            }
            return modelUID;
        });
    }
    drop(modelUID) {
        const uid = modelUID !== null && modelUID !== void 0 ? modelUID : this.modelUID;
        if ((scriptService.context === undefined) || (uid === undefined))
            return false;
        scriptService.context.pendingMessages.push(new DropModelMessage(uid));
        return true;
    }
    /// Private helper methods ///
    createModelInstance(uid) {
        if ((this.modelUID === undefined))
            return undefined;
        const model = Object.create(ScriptModel.prototype);
        model.modelUID = uid;
        return model;
    }
    getBroadcastProps() {
        const context = scriptService.context;
        if (context === undefined)
            throw new Error("[model] Script service has no context");
        if (context.pendingMessages === undefined)
            throw new Error("[model] Script service context has no pending message array");
        return {
            broadcast: true,
            server: false,
            peer: false,
            externalMessages: context.pendingMessages
        };
    }
}
